import React from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { useDispatch } from 'react-redux';
import MusicXML from 'Models/MusicXML';
import Phrase from 'Models/Phrase';
import TimeSignature from 'Models/TimeSignature';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import { TimingOffsetsConfig } from 'Models/EventStream';
import type { AppDispatch } from 'Reducers';
import type { PrerenderedPhraseData } from 'Types';
import { NotifyPhaserEvents, PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { MODE_CONVERSION_OPTIONS, type ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import {
  FINGERING_DISPLAY_LABELS,
  FINGERING_DISPLAY_LEVELS,
  isFingeringDisplay,
  type FingeringDisplay,
} from 'Fingering/display';
import FingeringReportControl from 'Fingering/components/FingeringReportControl';
import FingeringTimingReadout from 'Fingering/components/FingeringTimingReadout';
import { useLiveFingering } from 'Fingering/useLiveFingering';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { getScoreThemePalette, isScoreTheme, type ScoreTheme } from 'Utils/ScoreTheme';
import { Game } from './MainAppPhaser';
import musicXmlRaw58343 from 'Phaser/__fixtures__/phrases/58343/phrase.musicxml?raw';
import cursorData58343 from 'Phaser/__fixtures__/phrases/58343/cursor_data_1440.json';
import cursorDataTimesig58343 from 'Phaser/__fixtures__/phrases/58343/cursor_data_timesig_1440.json';
import musicXmlRaw58264 from 'Phaser/__fixtures__/phrases/58264/phrase.musicxml?raw';
import cursorData58264 from 'Phaser/__fixtures__/phrases/58264/cursor_data_1440.json';
import cursorDataTimesig58264 from 'Phaser/__fixtures__/phrases/58264/cursor_data_timesig_1440.json';
import musicXmlRaw58324 from 'Phaser/__fixtures__/phrases/58324/phrase.musicxml?raw';
import cursorData58324 from 'Phaser/__fixtures__/phrases/58324/cursor_data_1440.json';
import cursorDataTimesig58324 from 'Phaser/__fixtures__/phrases/58324/cursor_data_timesig_1440.json';

const TRANSPOSE_OPTIONS = Array.from({ length: 15 }, (_, index) => index - 7);

/** Finger numbers as the phrase files carry them, i.e. live finger numbers off. */
const PHRASE_FILE_FINGERING = 'file';

/** The level and tier the fixture phrases were written for. */
const FIXTURE_LEVEL_TIER = { level: 17, tier: 3 };

/** Three phrases of one tier, served in turn so consecutive phrases differ. */
const FIXTURE_PHRASES = [
  {
    musicXmlRaw: musicXmlRaw58343,
    cursorData: cursorData58343,
    cursorDataTimesig: cursorDataTimesig58343,
    numNotes: 13,
  },
  {
    musicXmlRaw: musicXmlRaw58264,
    cursorData: cursorData58264,
    cursorDataTimesig: cursorDataTimesig58264,
    numNotes: 13,
  },
  {
    musicXmlRaw: musicXmlRaw58324,
    cursorData: cursorData58324,
    cursorDataTimesig: cursorDataTimesig58324,
    numNotes: 15,
  },
];

const buildPhrase = (sequenceId: number, servedCount: number) => {
  const { musicXmlRaw, cursorData, cursorDataTimesig, numNotes } =
    FIXTURE_PHRASES[servedCount % FIXTURE_PHRASES.length];
  const musicXml = new MusicXML(musicXmlRaw, [new TimeSignature(3, 4)]);

  return new Phrase(
    `visual-transposition-${sequenceId}`,
    musicXml,
    numNotes,
    '',
    '',
    cursorData.serializedIterator as unknown as PrerenderedPhraseData[],
    cursorDataTimesig.serializedIterator as unknown as PrerenderedPhraseData[],
    cursorData.measureRects,
    FIXTURE_LEVEL_TIER,
  );
};

type FixtureFingering = FingeringDisplay | typeof PHRASE_FILE_FINGERING;

interface FixtureView {
  scoreTheme: ScoreTheme;
  narrow: boolean;
  zoom: number;
  fingering: FixtureFingering;
  transposeSemitones: number;
}

/** How the page is asked to start, from its address: `?theme=dark` for a score
 *  theme, `?layout=narrow` for the side-by-side phone layout, `?zoom=1` for a
 *  lesson zoom between 0.5 and 1, `?fingering=all` and `?transpose=1` for what
 *  the two controls start on. */
const fixtureView = (search: string): FixtureView => {
  const parameters = new URLSearchParams(search);
  const theme = parameters.get('theme');
  const fingering = parameters.get('fingering');
  const zoom = Number(parameters.get('zoom') ?? Number.NaN);
  const transposeSemitones = Number(parameters.get('transpose') ?? 0);
  return {
    scoreTheme: isScoreTheme(theme) ? theme : 'light',
    narrow: parameters.get('layout') === 'narrow',
    zoom: zoom >= 0.5 && zoom <= 1 ? zoom : 0.9,
    fingering: isFingeringDisplay(fingering) ? fingering : PHRASE_FILE_FINGERING,
    transposeSemitones: TRANSPOSE_OPTIONS.includes(transposeSemitones) ? transposeSemitones : 0,
  };
};

/** Development-only route that exercises the real two-phrase Phaser queue. */
const SightReadingTranspositionVisualFixture = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [view] = React.useState(() => fixtureView(window.location.search));
  const parentRef = React.useRef<HTMLElement | undefined>(undefined);
  const [dimensions, setDimensions] = React.useState<{ width: number; height: number }>();
  const [transposeSemitones, setTransposeSemitones] = React.useState(view.transposeSemitones);
  const [modeConversion, setModeConversion] = React.useState<ParallelModeConversion>('off');
  const [fingering, setFingering] = React.useState<FixtureFingering>(view.fingering);
  const fingeringDisplay = isFingeringDisplay(fingering) ? fingering : undefined;
  const liveFingeringRequested = fingeringDisplay !== undefined;
  const fingeringTimed = useFeatureFlag(FeatureFlag.ERGOBOT_TIMING);
  const { fingering: liveFingering, pending: fingeringPending } = useLiveFingering(
    fingeringDisplay,
    fingeringTimed,
  );
  const [rebuiltPhraseCount, setRebuiltPhraseCount] = React.useState<number | null>(null);
  const [eventStream] = React.useState(
    () => new EventStreamPhaser2(createTimeKeeper(), new TimingOffsetsConfig(), null),
  );

  React.useLayoutEffect(() => {
    const container = parentRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setDimensions({ width: rect.width, height: rect.height });
      }
    };
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(container);
    updateDimensions();
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const initialRequests: Array<{ sequenceId: number }> = [];
    let initialFillComplete = false;
    let servedCount = 0;
    const handlePhraseRequest = ({ sequenceId }: { sequenceId: number }) => {
      if (initialFillComplete) {
        NotifyPhaserEvents.PhraseReady({
          phrase: buildPhrase(sequenceId, servedCount++),
          sequenceId,
        });
        return;
      }

      initialRequests.push({ sequenceId });
      if (initialRequests.length < (view.narrow ? 3 : 2)) return;

      initialFillComplete = true;
      queueMicrotask(() => {
        initialRequests.forEach((request) => {
          NotifyPhaserEvents.PhraseReady({
            phrase: buildPhrase(request.sequenceId, servedCount++),
            sequenceId: request.sequenceId,
          });
        });
      });
    };

    PhaserToReactListeners.FetchPhrase(handlePhraseRequest, undefined, false, true);

    return () => RemoveListener('FetchPhrase', handlePhraseRequest);
    // The scene is rebuilt when live finger numbers are switched on or off, and
    // asks for its initial phrases again.
  }, [liveFingeringRequested, view.narrow]);

  React.useEffect(() => {
    if (fingeringDisplay === undefined) return;
    NotifyPhaserEvents.SetSightReadingFingeringDisplay({ display: fingeringDisplay });
  }, [fingeringDisplay]);

  React.useEffect(() => {
    NotifyPhaserEvents.SetSightReadingTranspose({
      semitones: transposeSemitones,
      modeConversion,
    });
  }, [modeConversion, transposeSemitones]);

  React.useEffect(() => {
    PhaserToReactListeners.SightReadingTransposeApplied(({ rebuiltPhraseCount: count }) => {
      setRebuiltPhraseCount(count);
    });
    return () => RemoveListener('SightReadingTransposeApplied');
  }, []);

  return (
    <Box
      data-sight-reading-transposition-fixture
      data-rebuilt-phrase-count={rebuiltPhraseCount ?? ''}
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: getScoreThemePalette(view.scoreTheme).scoreBackground,
      }}
    >
      <Box
        sx={{
          minHeight: '92px',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 4,
          color: '#ECECED',
          backgroundColor: '#050B13',
        }}
      >
        <Box sx={{ fontWeight: 700 }}>Sight-reading transposition</Box>
        <TextField
          select
          size="small"
          value={`${transposeSemitones}`}
          onChange={(event) => setTransposeSemitones(Number(event.target.value))}
          inputProps={{ 'aria-label': 'Sight-reading transposition' }}
          data-sight-reading-transpose-select
          sx={{ width: 150, backgroundColor: '#FFFFFF', borderRadius: 1 }}
        >
          {TRANSPOSE_OPTIONS.map((value) => (
            <MenuItem key={value} value={`${value}`} data-transpose-option={value}>
              {value === 0 ? 'Concert (0)' : `${value > 0 ? '+' : ''}${value}`}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          value={modeConversion}
          onChange={(event) => setModeConversion(event.target.value as ParallelModeConversion)}
          inputProps={{ 'aria-label': 'Sight-reading scale or mode' }}
          data-sight-reading-mode-select
          sx={{ width: 180, backgroundColor: '#FFFFFF', borderRadius: 1 }}
        >
          {MODE_CONVERSION_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          value={fingering}
          onChange={(event) => setFingering(event.target.value as FixtureFingering)}
          inputProps={{ 'aria-label': 'Finger numbers' }}
          data-sight-reading-fingering-select
          sx={{ width: 190, backgroundColor: '#FFFFFF', borderRadius: 1 }}
        >
          <MenuItem value={PHRASE_FILE_FINGERING}>Fingering: phrase file</MenuItem>
          {FINGERING_DISPLAY_LEVELS.map((level) => (
            <MenuItem key={level} value={level} data-fingering-option={level}>
              Fingering: {FINGERING_DISPLAY_LABELS[level].toLowerCase()}
            </MenuItem>
          ))}
        </TextField>
        {liveFingering && <FingeringReportControl />}
        <Box sx={{ opacity: 0.72 }}>Real Phaser renderer, two-phrase full queue</Box>
        {rebuiltPhraseCount !== null && (
          <Box data-transposition-rebuilt-count sx={{ opacity: 0.72 }}>
            Rebuilt phrases: {rebuiltPhraseCount}
          </Box>
        )}
      </Box>
      <Box
        ref={(node: HTMLElement | null) => {
          parentRef.current = node ?? undefined;
        }}
        sx={{ minHeight: 0, flex: 1, position: 'relative' }}
      >
        {liveFingering?.timed && <FingeringTimingReadout />}
        {dimensions && !fingeringPending && (
          <Game
            key={liveFingeringRequested ? 'live-fingering' : 'phrase-file-fingering'}
            eventStream={eventStream}
            width={dimensions.width}
            height={dimensions.height}
            togglePlayPause={() => {}}
            mobile={view.narrow}
            parentRef={parentRef}
            dispatch={dispatch}
            zoomLevel={view.zoom}
            goalTempo={72}
            ataEnabled={false}
            playing={false}
            freePlayMode={false}
            usingCustomGoalTempo={false}
            scoreTheme={view.scoreTheme}
            transposeSemitones={transposeSemitones}
            modeConversion={modeConversion}
            cursorIsVisible={false}
            fingering={liveFingering}
          />
        )}
      </Box>
    </Box>
  );
};

export default SightReadingTranspositionVisualFixture;
